import { Tooltip } from "components/Tooltip/Tooltip";
import { AnchorButton } from "components/Button/AnchorButton";
import { Button } from "components/Button/Button";
import { ButtonSubscribeToTeamPlanV2 } from "shared/ButtonSubscribeToTeamPlanV2";

import { useActiveContext } from "shared/ContextSwitcher/store";
import { useUserCanHaveFreeTeam } from "./hooks/useUserCanHaveFreeTeam";
import { formattedPlanName } from "lib/plans/plans";
import { useLoginUrl } from "lib/hooks/useLoginUrl";

import styles from "./ButtonSubscribeFromPricing.module.css";
import type { Origin } from "./PricingCards";

export function ButtonSubscribeFromPricing(props: {
  plan: { id: number; name: string };
}) {
  const { plan } = props;

  const userCanHaveFreeTeam = useUserCanHaveFreeTeam();
  const activeContext = useActiveContext();

  const loginUrl = useLoginUrl();

  if (!activeContext) {
    // Not signed in
    return (
      <AnchorButton size="M" theme="secondary" href={loginUrl}>
        Subscribe to {formattedPlanName(plan.name)}
      </AnchorButton>
    );
  }

  const contextType = activeContext.type;
  const contextPlanId = activeContext.serviceTier.id;

  if (contextPlanId === plan.id) {
    return (
      <Button size="M" theme="tertiary" disabled>
        Current plan
      </Button>
    );
  }

  if (activeContext?.serviceTier.hasCustomPlan) {
    return (
      <Tooltip
        position="below-center"
        className={styles.customPlanTooltip}
        label={`This ${activeContext.type} runs on a custom plan. To update plan or billing details, please reach out to us.`}
      >
        <AnchorButton size="M" theme="tertiary" href="mailto:sales@dune.com">
          Contact us
        </AnchorButton>
      </Tooltip>
    );
  }

  if (contextPlanId > plan.id && contextType === "team") {
    return (
      <ButtonSubscribeToTeamPlanV2
        plan={plan}
        getSubscribeUrl={getSubscribeUrl}
        getNewTeamUrl={getNewTeamUrl}
        theme="tertiary"
      >
        Downgrade to {formattedPlanName(plan.name)}
      </ButtonSubscribeToTeamPlanV2>
    );
  }

  if (plan.id === 1) {
    return contextType === "user" && !userCanHaveFreeTeam ? (
      <Tooltip
        label="You can't own more than 3 teams on the free plan."
        position="below-center"
      >
        <Button size="M" theme="tertiary" disabled>
          Continue with free
        </Button>
      </Tooltip>
    ) : (
      <ButtonSubscribeToTeamPlanV2
        plan={plan}
        getSubscribeUrl={getSubscribeUrl}
        getNewTeamUrl={getNewTeamUrl}
        theme="tertiary"
      >
        Subscribe to {formattedPlanName(plan.name)}
      </ButtonSubscribeToTeamPlanV2>
    );
  }

  return (
    <ButtonSubscribeToTeamPlanV2
      plan={plan}
      getSubscribeUrl={getSubscribeUrl}
      getNewTeamUrl={getNewTeamUrl}
      theme="secondary"
    >
      Subscribe to {formattedPlanName(plan.name)}
    </ButtonSubscribeToTeamPlanV2>
  );
}

const pricing: Origin = "pricing";

function getSubscribeUrl(team: { handle: string }, plan: { name: string }) {
  return `/t/${team.handle}/subscribe/${plan.name}?origin=${pricing}`;
}

const getNewTeamUrl = () => `/teams/new?origin=${pricing}`;
